import { useCallback, useEffect, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import {
	type ConversationStarterType,
	type RovoConvoStarterRecommendation,
} from '@atlassian/convo-ai-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { fetchAgentConvoStarters } from '../utils/fetchConvoStarters';

export const i18n = defineMessages({
	aiActionsStaticFallback1: {
		id: 'ai-smart-button.ai-actions.static-fallback-1',
		defaultMessage: 'What is Rovo Chat?',
		description: 'The text shown for the first static fallback conversation starter.',
	},
	aiActionsStaticFallback2: {
		id: 'ai-smart-button.ai-actions.static-fallback-2',
		defaultMessage: 'Help me get started with Rovo Chat',
		description: 'The text shown for the third static fallback conversation starter.',
	},
	aiActionsStaticAgentFallback1: {
		id: 'ai-smart-button.ai-actions.static-agent-fallback-1',
		defaultMessage: 'What are Rovo Agents?',
		description: 'The text shown for the second static fallback conversation starter.',
	},
	aiActionsStaticAgentFallback2: {
		id: 'ai-smart-button.ai-actions.static-agent-fallback-2',
		defaultMessage: 'How can I use Rovo Agents in my Confluence space?',
		description: 'The text shown for the fourth static fallback conversation starter.',
	},
});

export const ConversationStarterTypeEnum: { [K in ConversationStarterType]: K } = {
	AGENT: 'AGENT',
	CONTEXTUAL: 'CONTEXTUAL',
	STATIC: 'STATIC',
};

type UseFetchConvoStartersProps = {
	contentUrl: string;
	numberOfRecommendations: number;
	showConvoStarters?: boolean;
	triggerId: string;
};

type UseFetchConvoStartersReturnType = {
	convoStarters: RovoConvoStarterRecommendation[];
	error?: Error;
	isLoading: boolean;
};

export const useFetchConvoStarters = ({
	triggerId,
	numberOfRecommendations,
	contentUrl,
	showConvoStarters,
}: UseFetchConvoStartersProps): UseFetchConvoStartersReturnType => {
	const intl = useIntl();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [starters, setStarters] = useState<RovoConvoStarterRecommendation[]>([]);

	const makeConvoStartersRequest = useCallback(async () => {
		try {
			const suggestedRovoConvoStarterResponse = await fetchAgentConvoStarters({
				triggerId,
				numberOfRecommendations,
				contentUrl,
			});

			if (
				!suggestedRovoConvoStarterResponse ||
				suggestedRovoConvoStarterResponse.triggers[0].recommendations[0].recommendation_details
					.convo_starters.length === 0
			) {
				setError(new Error('No convo starters found'));
				return;
			}

			setStarters(
				suggestedRovoConvoStarterResponse.triggers[0].recommendations[0].recommendation_details
					.convo_starters,
			);
		} catch (err) {
			setError(new Error('Failed to load convo starters'));
		}
		setLoading(false);
	}, [triggerId, numberOfRecommendations, contentUrl]);

	useEffect(() => {
		if (showConvoStarters) {
			void makeConvoStartersRequest();
		}
	}, [makeConvoStartersRequest, showConvoStarters]);

	return useMemo(() => {
		const getConvoStarters = () => {
			if (error || !starters || starters.length === 0) {
				const staticConvoStarters: RovoConvoStarterRecommendation[] = [
					{
						agent_name: 'static-fallback-1',
						starter: intl.formatMessage(i18n.aiActionsStaticFallback1),
						agent_id: '',
						type: ConversationStarterTypeEnum.STATIC,
					},
					{
						agent_name: 'static-agent-fallback-1',
						starter: intl.formatMessage(i18n.aiActionsStaticAgentFallback1),
						agent_id: '',
						type: ConversationStarterTypeEnum.STATIC,
					},
					{
						agent_name: 'static-fallback-2',
						starter: intl.formatMessage(i18n.aiActionsStaticFallback2),
						agent_id: '',
						type: ConversationStarterTypeEnum.STATIC,
					},
					{
						agent_name: 'static-agent-fallback-2',
						starter: intl.formatMessage(i18n.aiActionsStaticAgentFallback2),
						agent_id: '',
						type: ConversationStarterTypeEnum.STATIC,
					},
				];

				return staticConvoStarters;
			}

			return starters;
		};

		return {
			convoStarters: !showConvoStarters || loading ? [] : getConvoStarters(),
			isLoading: loading,
			error,
		};
	}, [intl, starters, loading, error, showConvoStarters]);
};
